import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import type { Cell, Table as TanStackTable } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { type CSSProperties, type MouseEvent, memo, useMemo } from 'react';

import { cn } from '../../styles/lib/utils';
import type { GridSpacing } from '../Grid';
import { GridBody } from '../Grid';
import { GridCell } from '../GridCell';
import { GRID_ROW_HEIGHT_PX, GridRow } from '../GridRow';
import { SKELETON_ROW_HEIGHT, SkeletonLoader } from '../SkeletonLoader';
import { getPinnedCellProps, getRowStripeColors, type PinnedCellLayout, toCssVarName } from './utils';

type TableBodyProps<TData> = {
  table: TanStackTable<TData>;
  columnGroups: { left: string[]; center: string[]; right: string[] };
  activeId: string | null;
  /** Sticky layout of each visible pinned column. */
  pinnedLayouts: Map<string, PinnedCellLayout>;
  isLoading?: boolean;
  isFetching?: boolean;
  fixedPinning?: boolean;
  /** When using server-side selection (e.g. select all across pages), use this to determine if a row is selected. */
  getIsRowSelected?: (rowId: string) => boolean;
  /** Called when a row is clicked (for row-click selection). Receives the row index within the current page. */
  onRowClick?: (rowIndex: number, event: MouseEvent<HTMLTableRowElement>) => void;
  /** Row ids rendered with the highlighted (blue) background, independent of selection. */
  highlightedRowIds?: ReadonlySet<string>;
  /** Column id whose cells keep their width as flex basis and grow into leftover section space. */
  expandColumnId?: string;
  onRowHoverChange?: (rowId: string | null) => void;
  enableRowHoverHighlight?: boolean;
  /** Renders rows and cells for the data grid keyboard model (see useGridNavigation). */
  isGrid?: boolean;
  /** Rows before this page's first row, header rows included; sets aria-rowindex when the page is a subset. */
  ariaRowOffset?: number;
  /** Exposes each row's selected state as aria-selected. */
  isSelectable?: boolean;
  /** Sticks each expanded, can-expand row below the column header, stacked below any such rows above it. */
  stickyExpandedRows?: boolean;
  /** Row height, used to compute the stacking offset for `stickyExpandedRows`. */
  spacing?: GridSpacing;
};

const SECTIONS = ['left', 'center', 'right'] as const;

/**
 * Un-memoized normal table body component.
 * Renders each row's left-pinned, center and right-pinned cells in visual order.
 * Shows skeleton loaders during initial load or fetching.
 */
function TableBody<TData>({
  table,
  columnGroups,
  activeId,
  pinnedLayouts,
  isLoading = false,
  isFetching = false,
  fixedPinning = false,
  getIsRowSelected,
  onRowClick,
  highlightedRowIds,
  expandColumnId,
  onRowHoverChange,
  enableRowHoverHighlight = true,
  stickyExpandedRows = false,
  spacing = 'cozy',
  isGrid = false,
  ariaRowOffset,
  isSelectable = false,
}: TableBodyProps<TData>) {
  const gridCellProps = isGrid ? { role: 'gridcell', 'data-grid-cell': '', className: 'focus-ring-inset' } : undefined;
  const canResize = !!table.options.enableColumnResizing;
  // Sortable items per section (excluding select and active)
  const sortableItems = useMemo(
    () => ({
      left: columnGroups.left.filter((id) => id !== 'select' && id !== activeId),
      center: columnGroups.center.filter((id) => id !== activeId),
      right: columnGroups.right.filter((id) => id !== activeId),
    }),
    [columnGroups, activeId],
  );

  const allColumnIds = [...columnGroups.left, ...columnGroups.center, ...columnGroups.right];
  // Pushes the right-pinned cells to the row's end when the columns don't fill the table.
  const firstRightColumnId = columnGroups.right.find((id) => pinnedLayouts.has(id));
  const rightEdgeClass = cn('ml-auto', fixedPinning && 'pl-3');

  // Show skeleton rows during initial load (isLoading) OR when fetching with no data
  const shouldShowSkeletons = isLoading || (isFetching && table.getRowModel().rows.length === 0);

  if (shouldShowSkeletons) {
    // Show 25 skeleton rows during loading
    return (
      <GridBody>
        {Array.from({ length: 25 }).map((_, rowIndex) => {
          const rowColor = rowIndex % 2 === 0 ? 'white' : 'grey';

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
            <GridRow key={`skeleton-${rowIndex}`} color={rowColor}>
              {allColumnIds.map((columnId) => {
                const column = table.getAllColumns().find((col) => col.id === columnId);
                if (!column?.getIsVisible()) return null;
                const pinned = getPinnedCellProps(pinnedLayouts.get(columnId));
                const isExpand = columnId === expandColumnId;
                const widthStyles =
                  columnId !== 'select' && column
                    ? isExpand
                      ? { flexBasis: column.getSize(), width: 'auto', minWidth: 0, flexGrow: 1, flexShrink: 1 }
                      : { width: column.getSize() }
                    : undefined;

                return (
                  <GridCell
                    key={columnId}
                    data-pinned={pinned['data-pinned']}
                    className={cn(
                      columnId === 'select' ? 'w-[32px] justify-center' : undefined,
                      rowColor === 'white' ? 'bg-white' : 'bg-grey-10',
                      pinned.className,
                      columnId === firstRightColumnId && rightEdgeClass,
                    )}
                    style={{ ...widthStyles, ...pinned.style }}
                  >
                    <SkeletonLoader width={isExpand ? 0 : undefined} height={SKELETON_ROW_HEIGHT} className="flex-1" />
                  </GridCell>
                );
              })}
            </GridRow>
          );
        })}
      </GridBody>
    );
  }

  const rows = table.getRowModel().rows;
  const rowColors = getRowStripeColors(rows);

  // With stickyExpandedRows, each top-level row's group (itself + all descendants, which the
  // expanded row model already lays out contiguously) renders in its own <tbody>. That bounds a
  // sticky row's containing block to its own group, so it releases once that group scrolls past
  // instead of staying pinned until the entire table does. Without the flag, one <tbody> as before.
  const groupBoundaries = stickyExpandedRows
    ? rows.reduce<number[]>((boundaries, row, index) => {
        if (row.depth === 0) boundaries.push(index);
        return boundaries;
      }, [])
    : [0];

  // Count of expanded, can-expand rows seen so far within the current group — offsets each sticky
  // row below the ones already stacked above it in the same group. Reset at each group boundary.
  let expandedStackCount = 0;

  const renderRow = (row: (typeof rows)[number], index: number) => {
    const rowColor = rowColors[index];
    const isSelected = getIsRowSelected ? getIsRowSelected(row.id) : row.getIsSelected();
    const isHighlighted = highlightedRowIds?.has(row.id) ?? false;
    const effectiveRowColor = isSelected || isHighlighted ? 'blue' : rowColor;
    const isStickyExpanded = stickyExpandedRows && row.getCanExpand() && row.getIsExpanded();
    // Also stamped as data-sticky-row so the Table's focus reveal can tell a stuck row from one in flow.
    const stickyOffset = isStickyExpanded ? expandedStackCount * GRID_ROW_HEIGHT_PX[spacing] : undefined;
    const stickyStyle: CSSProperties | undefined =
      stickyOffset !== undefined
        ? { position: 'sticky', top: `calc(var(--sticky-header-h, 0px) + ${stickyOffset}px)`, zIndex: 5 }
        : undefined;
    if (isStickyExpanded) expandedStackCount += 1;
    // Blue rows skip the tint but keep `data-hovered` so their hover-revealed actions still work.
    const canHoverTint = effectiveRowColor !== 'blue' && enableRowHoverHighlight;
    const visibleCells = row.getVisibleCells();

    const renderCell = (cell: Cell<TData, unknown>) => {
      let widthStyles: CSSProperties | undefined =
        canResize && cell.column.id !== 'select'
          ? { width: `calc(var(--col-${toCssVarName(cell.column.id)}-size) * 1px)` }
          : cell.column.id !== 'select'
            ? { width: cell.column.getSize() }
            : undefined;
      // Auto width drops the configured size from the table's min-content; the column shrinks to its unwrapped content.
      if (expandColumnId && cell.column.id === expandColumnId) {
        widthStyles = {
          flexBasis: widthStyles?.width,
          width: 'auto',
          minWidth: 'max-content',
          flexGrow: 1,
          flexShrink: 1,
        };
      }

      const isDragging = activeId === cell.column.id;
      const pinned = getPinnedCellProps(pinnedLayouts.get(cell.column.id));
      const cellBgClass = cn(
        effectiveRowColor === 'blue' ? 'bg-blue-10' : effectiveRowColor === 'white' ? 'bg-white' : 'bg-grey-10',
        canHoverTint && 'group-data-[hovered=true]/row:bg-grey-20',
        pinned.className,
        cell.column.id === firstRightColumnId && rightEdgeClass,
      );

      // Show skeleton in cell during fetch (but not initial load)
      if (isFetching && !isLoading) {
        return (
          <GridCell
            key={cell.column.id}
            data-column-id={cell.column.id}
            data-pinned={pinned['data-pinned']}
            {...gridCellProps}
            className={cn(
              cell.column.id === 'select' ? 'w-[32px] justify-center' : undefined,
              cellBgClass,
              gridCellProps?.className,
            )}
            style={{ ...widthStyles, ...pinned.style }}
          >
            <SkeletonLoader height={SKELETON_ROW_HEIGHT} className="flex-1" />
          </GridCell>
        );
      }

      return (
        <GridCell
          key={cell.column.id}
          data-column-id={cell.column.id}
          data-pinned={pinned['data-pinned']}
          {...gridCellProps}
          className={cn(
            cell.column.id === 'select' ? 'w-[32px] justify-center' : undefined,
            cellBgClass,
            gridCellProps?.className,
          )}
          style={{ ...widthStyles, ...pinned.style, opacity: isDragging ? 0.5 : undefined }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </GridCell>
      );
    };

    const selectCell = visibleCells.find((c) => c.column.id === 'select');

    return (
      <GridRow
        key={row.id}
        color={effectiveRowColor}
        onClick={onRowClick ? (event) => onRowClick(index, event) : undefined}
        onMouseEnter={onRowHoverChange ? () => onRowHoverChange(row.id) : undefined}
        onFocus={onRowHoverChange ? () => onRowHoverChange(row.id) : undefined}
        data-row-id={row.id}
        data-grid-row={isGrid ? '' : undefined}
        aria-rowindex={isGrid && ariaRowOffset !== undefined ? ariaRowOffset + index + 1 : undefined}
        aria-selected={isGrid && isSelectable && row.getCanSelect() ? isSelected : undefined}
        data-sticky-row={stickyOffset}
        className={cn(canHoverTint && 'data-[hovered=true]:bg-grey-20', onRowClick && 'cursor-pointer')}
        style={stickyStyle}
      >
        {/* Select cell - always first, not sortable */}
        {selectCell && renderCell(selectCell)}

        {SECTIONS.map((section) => {
          const sectionCells = visibleCells.filter(
            (c) => c.column.id !== 'select' && columnGroups[section].includes(c.column.id),
          );
          if (sectionCells.length === 0) return null;
          return (
            <SortableContext
              key={section}
              id={`${section}Cells`}
              items={sortableItems[section]}
              strategy={horizontalListSortingStrategy}
            >
              {sectionCells.map(renderCell)}
            </SortableContext>
          );
        })}
      </GridRow>
    );
  };

  // Show real data with optional skeleton overlay for fetching state
  return (
    <>
      {groupBoundaries.map((start, groupIndex) => {
        const end = groupBoundaries[groupIndex + 1] ?? rows.length;
        const groupRows = rows.slice(start, end);
        expandedStackCount = 0;

        return (
          <GridBody key={groupRows[0]?.id ?? groupIndex}>
            {groupRows.map((row, rowIndexInGroup) => renderRow(row, start + rowIndexInGroup))}
          </GridBody>
        );
      })}
    </>
  );
}

/**
 * Special memoized wrapper for the table body that we use during column resizing and drag operations.
 * This prevents expensive re-renders of all cells while a column is being resized or dragged.
 * During these operations, only the data itself matters - visual drag/resize feedback is handled
 * by the DragOverlay and CSS, so we don't need cells to re-render.
 */
const MemoizedTableBody = memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof TableBody;

export { MemoizedTableBody, TableBody };
